import { useInfiniteQuery } from "@tanstack/react-query";
import type { QueryFunctionContext, InfiniteData } from "@tanstack/react-query";
import cmsClient from "src/services/cmsClient";
import { adaptSocialPosts } from "../../adapters/adaptSocialPosts";

export interface UseSocialPostsOptions {
	search: string;
	hashtag: string;
	author: string;
	startDate: string;
	endDate: string;
	limit?: number;
	enabled?: boolean;
}

const fetchSocialPosts = async (
	options: UseSocialPostsOptions,
	nextPageUrl?: string
) => {
	const url = nextPageUrl ?? "/social-posts/";

	const params: Record<string, string | number> = {};
	if (!nextPageUrl) {
		params.limit = options.limit ?? 12;
		if (options.hashtag) params.hashtag = options.hashtag;
		if (options.author) params.author = options.author;
		if (options.search) params.search = options.search;
		if (options.startDate) params.start_date = options.startDate;
		if (options.endDate) params.end_date = options.endDate;
	}

	const { data } = await cmsClient.get<CMSPaginatedData<ISocialPostFull>>(url, {
		params: nextPageUrl ? undefined : params
	});

	return data;
};

const useSocialPosts = ({
	search,
	hashtag,
	author,
	startDate,
	endDate,
	enabled = true,
	limit = 12
}: UseSocialPostsOptions) => {
	const queryKey = [
		"social-posts",
		{ search, hashtag, author, startDate, endDate }
	];

	const queryFn = (context: QueryFunctionContext) => {
		const params = { search, hashtag, author, startDate, endDate, limit };
		return fetchSocialPosts(params, context.pageParam);
	};

	const getNextPageParam = (lastPage: CMSPaginatedData<ISocialPostFull>) => {
		return lastPage.next;
	};

	const select = (data: InfiniteData<CMSPaginatedData<ISocialPostFull>>) => {
		return {
			...data,
			pages: data.pages.map((page) => ({
				...page,
				results: adaptSocialPosts(page.results)
			}))
		};
	};

	return useInfiniteQuery({
		queryKey,
		queryFn,
		getNextPageParam,
		enabled,
		select
	});
};

export default useSocialPosts;
